import React, { useState, useEffect, useRef } from 'react';
import { X, Truck, FileText, CheckCircle2, AlertCircle, RefreshCw, Send, ShieldCheck, HelpCircle, Search } from 'lucide-react';
import { fiscalService } from '../../services/fiscalService';
import { EmpresaFiscal } from '../../types/fiscal';
import { supabase } from '../../lib/supabase';
import { MunicipioUfSelect } from '../common/MunicipioUfSelect';
import { CfopAutocomplete } from '../common/CfopAutocomplete';
import { buscarDadosCnpj } from '../../lib/cnpjLookup';

interface EmitirCteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialOs?: any; // Se aberto diretamente da OS
}

export function EmitirCteModal({ isOpen, onClose, onSuccess, initialOs }: EmitirCteModalProps) {
  const [empresas, setEmpresas] = useState<EmpresaFiscal[]>([]);
  const [empresaId, setEmpresaId] = useState<number>(1);
  const [selectedEmpresa, setSelectedEmpresa] = useState<EmpresaFiscal | null>(null);

  // Seleção de OS (se não vier pré-carregada)
  const [osList, setOsList] = useState<any[]>([]);
  const [selectedOsId, setSelectedOsId] = useState<string>(initialOs ? String(initialOs.id) : '');
  const [currentOs, setCurrentOs] = useState<any>(initialOs || null);

  // Campos do CT-e
  const [naturezaOperacao, setNaturezaOperacao] = useState('PRESTACAO DE SERVICO DE TRANSPORTE');
  const [cfop, setCfop] = useState('5353');
  // Enquanto o usuário não editar "Natureza da Operação" na mão, o campo
  // acompanha a descrição oficial do CFOP escolhido (Focus NFe). No momento
  // em que ele digita algo diretamente ali, para de sobrescrever.
  const naturezaEditadaManualmente = useRef(false);
  const [municipioInicio, setMunicipioInicio] = useState('');
  const [ufInicio, setUfInicio] = useState('');
  const [codigoIbgeInicio, setCodigoIbgeInicio] = useState('');
  const [municipioFim, setMunicipioFim] = useState('');
  const [ufFim, setUfFim] = useState('');
  const [codigoIbgeFim, setCodigoIbgeFim] = useState('');

  // Remetente / Destinatário
  const [remetenteNome, setRemetenteNome] = useState('');
  const [remetenteCnpj, setRemetenteCnpj] = useState('');
  const [remetenteCep, setRemetenteCep] = useState('');
  const [destinatarioNome, setDestinatarioNome] = useState('');
  const [destinatarioCnpj, setDestinatarioCnpj] = useState('');
  const [destinatarioCep, setDestinatarioCep] = useState('');
  const [buscandoCnpjRemetente, setBuscandoCnpjRemetente] = useState(false);
  const [buscandoCnpjDestinatario, setBuscandoCnpjDestinatario] = useState(false);

  // Veículo e Motorista
  const [placa, setPlaca] = useState('BRA2E19');
  const [renavam, setRenavam] = useState('');
  const [motoristaNome, setMotoristaNome] = useState('');
  const [motoristaCpf, setMotoristaCpf] = useState('');
  const [rntrc, setRntrc] = useState('');

  // Carga e Valores
  const [valorFrete, setValorFrete] = useState<number>(500);
  const [valorCarga, setValorCarga] = useState<number>(5000);
  const [pesoKg, setPesoKg] = useState<number>(250);
  const [produtoPredominante, setProdutoPredominante] = useState('Cargas Gerais');
  const [chaveNfe, setChaveNfe] = useState('');

  // Status de Envio
  const [loading, setLoading] = useState(false);
  const [loadingOs, setLoadingOs] = useState(false);
  const [resultado, setResultado] = useState<any | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      carregarEmpresas();
      if (!initialOs) {
        carregarUltimasOs();
      } else {
        aplicarDadosOs(initialOs);
      }
    }
  }, [isOpen, initialOs]);

  const carregarEmpresas = async () => {
    const list = await fiscalService.getEmpresasFiscais();
    setEmpresas(list);
    if (list.length > 0) {
      setEmpresaId(list[0].id);
      setSelectedEmpresa(list[0]);
      if (list[0].rntrc) setRntrc(list[0].rntrc);
    }
  };

  const carregarUltimasOs = async () => {
    try {
      setLoadingOs(true);
      // A OS não tem cliente_nome/veiculo_placa/motorista_nome: o cliente vem de
      // pedido -> pessoa, a placa é `placa` e o condutor é `agregado` (id de pessoa).
      // Pedir as colunas antigas derrubava a consulta inteira (42703).
      const { data } = await supabase
        .from('ordem_de_servico')
        .select('id, descricao, retirada, entrega, total_os, placa, agregado, pedido:pedido_id ( pessoa:pessoa_id ( nome ) )')
        .order('id', { ascending: false })
        .limit(25);

      const linhas = data || [];

      // Resolve o nome dos condutores em uma única consulta.
      const agregadoIds = Array.from(
        new Set(linhas.map((os: any) => Number(os.agregado)).filter((n) => n && !isNaN(n)))
      );
      const nomePorPessoa = new Map<number, string>();
      if (agregadoIds.length > 0) {
        const { data: pessoas } = await supabase
          .from('pessoa')
          .select('id, nome')
          .in('id', agregadoIds);
        (pessoas || []).forEach((p: any) => nomePorPessoa.set(Number(p.id), p.nome));
      }

      setOsList(
        linhas.map((os: any) => ({
          ...os,
          cliente_nome: os.pedido?.pessoa?.nome || '',
          veiculo_placa: os.placa || '',
          motorista_nome: nomePorPessoa.get(Number(os.agregado)) || '',
        }))
      );
    } catch (e) {
      console.warn('Erro ao carregar OSs:', e);
    } finally {
      setLoadingOs(false);
    }
  };

  const handleSelectOs = async (osIdStr: string) => {
    setSelectedOsId(osIdStr);
    if (!osIdStr) {
      setCurrentOs(null);
      return;
    }
    const osId = Number(osIdStr);
    const { data } = await supabase
      .from('ordem_de_servico')
      .select('*')
      .eq('id', osId)
      .single();
    if (data) {
      setCurrentOs(data);
      aplicarDadosOs(data);
    }
  };

  // A OS em si não guarda CNPJ/CEP/cidade/UF do cliente nem CPF do motorista —
  // esses dados vêm de pedido -> pessoa (cliente) e pessoa_endereco -> cidade
  // -> estado (endereço), e de pessoa (agregado) para o motorista. Os nomes
  // de campo antigos usados aqui (cliente_cnpj, retirada_cidade, uf_origem,
  // motorista_cpf etc.) nunca existiram na tabela, então o formulário sempre
  // abria com CNPJ/CEP/motorista em branco — causa mais provável dos erros
  // de "CEP, CNPJ, estado e cidade" relatados na emissão.
  const aplicarDadosOs = async (os: any) => {
    setPlaca(os.veiculo_placa || os.placa || '');
    setMotoristaNome(os.motorista_nome || '');

    const frete = Number(os.total_os || os.valor_total || 0);
    setValorFrete(frete);
    setValorCarga(frete > 0 ? frete * 3 : 0);
    setProdutoPredominante(os.descricao || 'Cargas Gerais');

    try {
      let pessoaId: number | null = null;
      if (os.pedido_id) {
        const { data: pedido } = await supabase
          .from('pedido')
          .select('pessoa_id')
          .eq('id', os.pedido_id)
          .maybeSingle();
        pessoaId = pedido?.pessoa_id || null;
      }

      if (pessoaId) {
        const { data: pessoa } = await supabase
          .from('pessoa')
          .select('nome, documento')
          .eq('id', pessoaId)
          .maybeSingle();

        // Só existe um "lado" cadastrado por OS (o cliente do pedido) — por
        // padrão preenche remetente e destinatário com ele; o usuário ajusta
        // manualmente se a operação tiver terceiros diferentes.
        if (pessoa) {
          setRemetenteNome(pessoa.nome || '');
          setRemetenteCnpj(pessoa.documento || '');
          setDestinatarioNome(pessoa.nome || '');
          setDestinatarioCnpj(pessoa.documento || '');
        }

        // Vários cadastros têm mais de um endereço sem nenhum marcado como
        // "principal" — pedir só 1 com order+limit nesse caso devolve
        // qualquer um dos dois (inclusive um sem CEP preenchido). Busca todos
        // e prioriza, na própria consulta, o que realmente tem CEP.
        const { data: enderecos } = await supabase
          .from('pessoa_endereco')
          .select('cep, principal')
          .eq('pessoa_id', pessoaId);
        const melhorEndereco = (enderecos || []).find((e) => e.principal === 'S' && e.cep)
          || (enderecos || []).find((e) => !!e.cep)
          || (enderecos || [])[0];

        if (melhorEndereco?.cep) {
          setRemetenteCep(melhorEndereco.cep);
          setDestinatarioCep(melhorEndereco.cep);
        }
      }

      if (os.agregado) {
        const { data: motorista } = await supabase
          .from('pessoa')
          .select('nome, documento')
          .eq('id', os.agregado)
          .maybeSingle();
        if (motorista) {
          setMotoristaNome(motorista.nome || os.motorista_nome || '');
          setMotoristaCpf(motorista.documento || '');
        }
      }
    } catch (err) {
      console.warn('Erro ao carregar dados do cliente/motorista para o CT-e:', err);
    }

    // Município/UF de início e fim da prestação não têm origem estruturada
    // (retirada/entrega são texto livre) — o usuário confirma/seleciona pela
    // lista abaixo; o CFOP interestadual (6353) é decidido quando ele escolhe.
  };

  const handleEmpresaChange = (id: number) => {
    setEmpresaId(id);
    const emp = empresas.find(e => e.id === id) || null;
    setSelectedEmpresa(emp);
    if (emp?.rntrc) setRntrc(emp.rntrc);
  };

  const handleBuscarCnpjRemetente = async () => {
    setBuscandoCnpjRemetente(true);
    try {
      const dados = await buscarDadosCnpj(remetenteCnpj);
      if (!dados) {
        setErro('CNPJ do remetente não encontrado na Receita Federal / Focus NFe.');
        return;
      }
      setRemetenteNome(dados.razaoSocial || remetenteNome);
      if (dados.cep) setRemetenteCep(dados.cep);
    } catch (err: any) {
      setErro(err.message || 'Erro ao consultar CNPJ do remetente.');
    } finally {
      setBuscandoCnpjRemetente(false);
    }
  };

  const handleBuscarCnpjDestinatario = async () => {
    setBuscandoCnpjDestinatario(true);
    try {
      const dados = await buscarDadosCnpj(destinatarioCnpj);
      if (!dados) {
        setErro('CNPJ do destinatário não encontrado na Receita Federal / Focus NFe.');
        return;
      }
      setDestinatarioNome(dados.razaoSocial || destinatarioNome);
      if (dados.cep) setDestinatarioCep(dados.cep);
    } catch (err: any) {
      setErro(err.message || 'Erro ao consultar CNPJ do destinatário.');
    } finally {
      setBuscandoCnpjDestinatario(false);
    }
  };

  const handleEmitir = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErro(null);
    setResultado(null);

    if (!codigoIbgeInicio || !codigoIbgeFim) {
      setErro('Selecione o Município Início e o Município Fim na lista para obter os códigos IBGE corretos (obrigatórios para o CT-e).');
      setLoading(false);
      return;
    }

    const remetenteCepLimpo = remetenteCep.replace(/\D/g, '');
    const destinatarioCepLimpo = destinatarioCep.replace(/\D/g, '');
    if (remetenteCepLimpo.length !== 8 || destinatarioCepLimpo.length !== 8) {
      setErro('Informe o CEP (8 dígitos) do Remetente e do Destinatário — a SEFAZ exige esse campo para autorizar o CT-e.');
      setLoading(false);
      return;
    }

    const remetenteDocLimpo = remetenteCnpj.replace(/\D/g, '');
    const destinatarioDocLimpo = destinatarioCnpj.replace(/\D/g, '');

    try {
      const payloadFocus = {
        cfop,
        natureza_operacao: naturezaOperacao,
        data_emissao: new Date().toISOString().split('T')[0],
        tipo_documento: 1,
        tipo_servico: 0,
        forma_emissao: 1,
        codigo_municipio_envio: codigoIbgeInicio,
        municipio_envio: municipioInicio,
        uf_envio: ufInicio,

        codigo_municipio_inicio: codigoIbgeInicio,
        municipio_inicio: municipioInicio,
        uf_inicio: ufInicio,

        codigo_municipio_fim: codigoIbgeFim,
        municipio_fim: municipioFim,
        uf_fim: ufFim,

        tomador: 3, // 3: Remetente

        remetente: {
          nome: remetenteNome,
          // CPF (11 dígitos) e CNPJ (14 dígitos) são campos distintos na Focus
          // NFe — enviar um CPF na chave "cnpj" faz a SEFAZ rejeitar o CT-e.
          ...(remetenteDocLimpo.length === 11 ? { cpf: remetenteDocLimpo } : { cnpj: remetenteDocLimpo }),
          inscricao_estadual: 'ISENTO', // Normalmente exigido pela SEFAZ
          logradouro: currentOs?.retirada || 'Não Informado',
          numero: 'S/N',
          bairro: 'Centro',
          cep: remetenteCepLimpo,
          codigo_municipio: codigoIbgeInicio,
          municipio: municipioInicio,
          uf: ufInicio,
          pais: 'Brasil',
        },

        destinatario: {
          nome: destinatarioNome,
          ...(destinatarioDocLimpo.length === 11 ? { cpf: destinatarioDocLimpo } : { cnpj: destinatarioDocLimpo }),
          inscricao_estadual: 'ISENTO',
          logradouro: currentOs?.entrega || 'Não Informado',
          numero: 'S/N',
          bairro: 'Centro',
          cep: destinatarioCepLimpo,
          codigo_municipio: codigoIbgeFim,
          municipio: municipioFim,
          uf: ufFim,
          pais: 'Brasil',
        },

        valor_total: valorFrete,
        valor_receber: valorFrete,
        componentes_valor: [
          {
            nome: 'FRETE PESO / VALOR',
            valor: valorFrete,
          }
        ],

        carga: {
          valor_carga: valorCarga,
          produto_predominante: produtoPredominante,
          informacoes_quantidade_carga: [
            {
              codigo_unidade_medida: '01',
              tipo_medida: 'PESO BRUTO',
              quantidade: pesoKg,
            }
          ]
        },

        modal_rodoviario: {
          rntrc: rntrc.replace(/\D/g, ''),
          veiculos: [
            {
              placa: placa.replace(/[^a-zA-Z0-9]/g, '').toUpperCase(),
              renavam: renavam.replace(/\D/g, ''),
              uf: ufInicio,
              tipo_veiculo: 1,
            }
          ],
          motoristas: [
            {
              nome: motoristaNome,
              cpf: motoristaCpf.replace(/\D/g, ''),
            }
          ]
        },

        icms: {
          icms_simples_nacional: {
            cst: '102',
            aliquota: 0,
            valor: 0,
          }
        }
      };

      const res = await fiscalService.emitirCte({
        empresa_id: empresaId,
        referencia: currentOs ? `CTE-OS-${currentOs.id}-${Date.now().toString().slice(-4)}` : undefined,
        documento_fiscal: {
          valor_total: valorFrete,
          valor_liquido: valorFrete,
        },
        origem: currentOs ? { tipo: 'ordem_de_servico', id: currentOs.id } : undefined,
        focus_payload: payloadFocus,
      });

      if (res.sucesso) {
        setResultado(res);
        if (onSuccess) onSuccess();
      } else {
        setErro(res.mensagem || 'Falha ao emitir CT-e na SEFAZ.');
      }
    } catch (err: any) {
      setErro(err.message || 'Erro inesperado.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Cabeçalho */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center text-white font-bold">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Emissão de CT-e (Conhecimento de Transporte)</h2>
              <p className="text-xs text-slate-300">
                Modelo 57 • SEFAZ Autorizadora • Integrado à Focus NFe
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Corpo do Formulário com Scroll */}
        <form onSubmit={handleEmitir} className="p-6 space-y-6 overflow-y-auto flex-1 text-sm text-slate-800">
          {/* Feedback de Sucesso */}
          {resultado && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-2 animate-in fade-in">
              <div className="flex items-center gap-2 font-bold text-emerald-800">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                {resultado.status === 'processando' ? 'CT-e Enviado para Processamento na SEFAZ!' : 'CT-e Autorizado com Sucesso!'}
              </div>
              <p className="text-xs text-emerald-700">
                Referência: <span className="font-mono font-bold">{resultado.referencia}</span> | ID do Documento: #{resultado.documento_id}
              </p>
              <p className="text-xs text-emerald-600">
                {resultado.mensagem}
              </p>
              <div className="pt-2 flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold"
                >
                  Concluir e Voltar
                </button>
              </div>
            </div>
          )}

          {/* Feedback de Erro */}
          {erro && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 flex items-start gap-2.5">
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-xs text-rose-800">Não foi possível emitir o CT-e:</p>
                <p className="text-xs text-rose-700 mt-0.5">{erro}</p>
              </div>
            </div>
          )}

          {/* 1. Origem e Empresa Emissora */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Empresa Emissora:
              </label>
              <select
                value={empresaId}
                onChange={(e) => handleEmpresaChange(Number(e.target.value))}
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs font-semibold text-slate-800 focus:outline-none focus:border-red-500"
              >
                {empresas.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.razao_social} ({emp.cnpj})
                  </option>
                ))}
              </select>
              <div className="mt-1 flex items-center gap-1.5 text-2xs text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                Ambiente SEFAZ:{' '}
                <span className={`font-bold uppercase ${selectedEmpresa?.ambiente_emissao === 'producao' ? 'text-red-600' : 'text-amber-600'}`}>
                  {selectedEmpresa?.ambiente_emissao || 'homologacao'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Importar de Ordem de Serviço (Opcional):
              </label>
              <select
                value={selectedOsId}
                onChange={(e) => handleSelectOs(e.target.value)}
                disabled={loadingOs}
                className="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-800 focus:outline-none focus:border-red-500 disabled:opacity-50"
              >
                <option value="">Selecione uma OS para pré-preencher...</option>
                {initialOs && (
                  <option value={String(initialOs.id)}>
                    OS #{initialOs.id} - {initialOs.cliente_nome || 'Cliente'} (Atual)
                  </option>
                )}
                {osList.map((os) => (
                  <option key={os.id} value={String(os.id)}>
                    OS #{os.id} - {os.cliente_nome || 'Cliente'} (R$ {os.total_os || 0})
                  </option>
                ))}
              </select>
              <span className="text-2xs text-slate-400 mt-1 block">
                Ao selecionar a OS, preenche cliente, valores, veículo e rotas automaticamente.
              </span>
            </div>
          </div>

          {/* 2. Rota e Operação */}
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b pb-1">
              <FileText className="w-4 h-4 text-red-600" />
              Dados da Prestação e CFOP
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-2xs font-semibold text-slate-600 mb-1">CFOP:</label>
                <CfopAutocomplete
                  value={cfop}
                  onChange={setCfop}
                  onSelectDescricao={(descricao) => {
                    // A Focus NFe devolve a descrição com o código na frente
                    // (ex.: "5353 - Prestação de serviço..."); remove esse
                    // prefixo para não duplicar o CFOP dentro do texto.
                    const semPrefixo = descricao.replace(/^\d{4}\s*-\s*/, '');
                    if (!naturezaEditadaManualmente.current) setNaturezaOperacao(semPrefixo.toUpperCase());
                  }}
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono"
                  placeholder="5353"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-2xs font-semibold text-slate-600 mb-1">
                  Natureza da Operação:
                  <span className="font-normal text-slate-400 normal-case"> (preenchida pelo CFOP - pode editar)</span>
                </label>
                <input
                  type="text"
                  value={naturezaOperacao}
                  onChange={(e) => {
                    naturezaEditadaManualmente.current = true;
                    setNaturezaOperacao(e.target.value);
                  }}
                  className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <MunicipioUfSelect
                label="Município Início (Origem)"
                required
                value={{ municipio: municipioInicio, uf: ufInicio, codigoIbge: codigoIbgeInicio }}
                onChange={(v) => {
                  setMunicipioInicio(v.municipio);
                  setUfInicio(v.uf);
                  setCodigoIbgeInicio(v.codigoIbge);
                }}
              />
              <MunicipioUfSelect
                label="Município Fim (Destino)"
                required
                value={{ municipio: municipioFim, uf: ufFim, codigoIbge: codigoIbgeFim }}
                onChange={(v) => {
                  setMunicipioFim(v.municipio);
                  setUfFim(v.uf);
                  setCodigoIbgeFim(v.codigoIbge);
                }}
              />
            </div>
          </div>

          {/* 3. Envolvidos (Remetente e Destinatário) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
              <div className="text-xs font-bold text-slate-700">Remetente (Expedidor)</div>
              <div>
                <label className="block text-2xs text-slate-500">Razão Social / Nome:</label>
                <input
                  type="text"
                  value={remetenteNome}
                  onChange={(e) => setRemetenteNome(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">CNPJ / CPF:</label>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={remetenteCnpj}
                    onChange={(e) => setRemetenteCnpj(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                  />
                  <button
                    type="button"
                    onClick={handleBuscarCnpjRemetente}
                    disabled={buscandoCnpjRemetente}
                    title="Buscar dados na Receita Federal (via Focus NFe)"
                    className="shrink-0 px-2.5 bg-slate-900 hover:bg-black text-white rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    {buscandoCnpjRemetente ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-2xs text-slate-500">CEP:</label>
                <input
                  type="text"
                  value={remetenteCep}
                  onChange={(e) => setRemetenteCep(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                />
              </div>
            </div>

            <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
              <div className="text-xs font-bold text-slate-700">Destinatário (Recebedor)</div>
              <div>
                <label className="block text-2xs text-slate-500">Razão Social / Nome:</label>
                <input
                  type="text"
                  value={destinatarioNome}
                  onChange={(e) => setDestinatarioNome(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">CNPJ / CPF:</label>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={destinatarioCnpj}
                    onChange={(e) => setDestinatarioCnpj(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                  />
                  <button
                    type="button"
                    onClick={handleBuscarCnpjDestinatario}
                    disabled={buscandoCnpjDestinatario}
                    title="Buscar dados na Receita Federal (via Focus NFe)"
                    className="shrink-0 px-2.5 bg-slate-900 hover:bg-black text-white rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    {buscandoCnpjDestinatario ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-2xs text-slate-500">CEP:</label>
                <input
                  type="text"
                  value={destinatarioCep}
                  onChange={(e) => setDestinatarioCep(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                />
              </div>
            </div>
          </div>

          {/* 4. Modal Rodoviário (Veículo e Motorista) */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <h4 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              <Truck className="w-4 h-4 text-red-600" />
              Modal Rodoviário
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-2xs text-slate-500">Placa:</label>
                <input
                  type="text"
                  value={placa}
                  onChange={(e) => setPlaca(e.target.value.toUpperCase())}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono font-bold uppercase"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">RNTRC Transportadora:</label>
                <input
                  type="text"
                  value={rntrc}
                  onChange={(e) => setRntrc(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                  placeholder="00000000"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">Motorista:</label>
                <input
                  type="text"
                  value={motoristaNome}
                  onChange={(e) => setMotoristaNome(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">CPF Motorista:</label>
                <input
                  type="text"
                  value={motoristaCpf}
                  onChange={(e) => setMotoristaCpf(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                  placeholder="000.000.000-00"
                />
              </div>
            </div>
          </div>

          {/* 5. Carga e Valores */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <h4 className="text-xs font-bold text-slate-700 uppercase flex items-center gap-1.5">
              Carga & Valores do Frete
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-2xs text-slate-500 font-bold text-red-600">Valor do Frete (R$):</label>
                <input
                  type="number"
                  step="0.01"
                  value={valorFrete}
                  onChange={(e) => setValorFrete(Number(e.target.value))}
                  className="w-full bg-white border-2 border-red-300 rounded-lg px-2.5 py-1.5 text-xs font-bold text-slate-900"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">Valor Carga (R$):</label>
                <input
                  type="number"
                  step="0.01"
                  value={valorCarga}
                  onChange={(e) => setValorCarga(Number(e.target.value))}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">Peso Bruto (KG):</label>
                <input
                  type="number"
                  step="0.1"
                  value={pesoKg}
                  onChange={(e) => setPesoKg(Number(e.target.value))}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono"
                />
              </div>
              <div>
                <label className="block text-2xs text-slate-500">Produto Predominante:</label>
                <input
                  type="text"
                  value={produtoPredominante}
                  onChange={(e) => setProdutoPredominante(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs"
                />
              </div>
            </div>
          </div>
        </form>

        {/* Rodapé com Ações */}
        <div className="bg-slate-100 px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold cursor-pointer"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={handleEmitir}
            disabled={loading}
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold shadow-md shadow-red-200 flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Transmitindo à SEFAZ...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Transmitir e Emitir CT-e
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
